import { useCallback, useEffect, useState } from 'react';

import { fg } from '@atlaskit/platform-feature-flags';
import { useAnalytics } from '@atlassian/conversation-assistant-instrumentation/controllers/analytics';
import { logException } from '@atlassian/conversation-assistant-instrumentation/services/sentry';
import { JSM_CREATE_KB_ARTICLES_KEY } from '@atlassian/conversation-assistant-message-actions-api/constants';
import { useChatContextStoreActions } from '@atlassian/conversation-assistant-store/controllers/chat-context/store';
import { JSM_CREATE_KB_STAGING_AREA_CLIENT_ID } from '@atlassian/rovo-content-bridge-api-commands/clients/jsm-create-kb-client';
import { ROVO_CHAT_CLIENT_ID } from '@atlassian/rovo-content-bridge-api-commands/clients/rovo-chat-client';
import {
	type JSMKBCreateItemsAction,
	JSMKBStagingAreaEventCommand,
	type JSMKBStagingAreaEventCommandProperties,
	type KBArticleData,
} from '@atlassian/rovo-content-bridge-api-commands/commands/jsm-create-kb-sync-commands';
import type { RovoContentBridgeSetupHandler } from '@atlassian/rovo-content-bridge-api/create-bridge-client-types';

import { createKbApi } from './createKbApi';

const isKBArticleData = (value: unknown): value is KBArticleData => {
	if (!value || typeof value !== 'object') {
		return false;
	}

	const candidate = value as Partial<KBArticleData>;
	return (
		typeof candidate.articleId === 'string' &&
		typeof candidate.title === 'string' &&
		Object.prototype.hasOwnProperty.call(candidate, 'body')
	);
};

const toKbDraftArticlesContext = (
	articles: KBArticleData[],
): Array<{ articleId: string; isEdited: boolean; title: string }> =>
	articles
		.filter(
			(article) =>
				typeof article.articleId === 'string' &&
				article.articleId.length > 0 &&
				typeof article.title === 'string',
		)
		.map((article) => ({
			articleId: article.articleId,
			isEdited: Boolean((article as KBArticleData & { isEdited?: boolean }).isEdited),
			title: article.title,
		}));

const toPublishableKbArticles = (articles: KBArticleData[]): KBArticleData[] =>
	articles.map((article) => ({
		articleId: article.articleId,
		title: article.title,
		summary: article.summary,
		body: article.body,
		rovoTopicId: article.rovoTopicId,
	}));

export const useCreateKbStagingAreaSync = ({
	articleIds,
	projectId,
	activationId,
	isLastActionMessage,
}: {
	articleIds: string[];
	projectId: number;
	activationId?: string;
	isLastActionMessage?: boolean;
}): {
	stagingAreaArticles: KBArticleData[];
	setupHandler: RovoContentBridgeSetupHandler;
} => {
	const [stagingAreaArticles, setStagingAreaArticles] = useState<KBArticleData[]>([]);
	const { setAIFeatureContext } = useChatContextStoreActions();
	const { sendAnalyticsEvent } = useAnalytics();

	const syncArticlesByIds = useCallback(
		async (articleIdsToSync: string[]) => {
			if (!activationId || !articleIdsToSync.length) {
				return;
			}

			const fetchedArticleResults = await Promise.allSettled(
				articleIdsToSync.map((id) =>
					createKbApi.fetchRovoSkillArticleById({ projectId, activationId, articleId: id }),
				),
			);
			const fetchedArticles = fetchedArticleResults
				.filter(
					(
						result,
					): result is PromiseFulfilledResult<
						Awaited<ReturnType<typeof createKbApi.fetchRovoSkillArticleById>>
					> => result.status === 'fulfilled',
				)
				.map((result) => result.value);

			const resolvedArticles: KBArticleData[] = fetchedArticles.map((article) => ({
				articleId: article.articleId,
				title: article.title,
				summary: '',
				body: article.body ?? null,
				rovoTopicId: article.rovoTopicId,
			}));
			const publishableArticles = toPublishableKbArticles(resolvedArticles);

			if (!publishableArticles.length) {
				return;
			}

			setStagingAreaArticles(publishableArticles);
			setAIFeatureContext({
				contextKey: JSM_CREATE_KB_ARTICLES_KEY,
				setContext: <T>(_existingContext: T): T =>
					({ kbDraftArticles: toKbDraftArticlesContext(resolvedArticles) }) as T,
			});
		},
		[projectId, activationId, setAIFeatureContext],
	);

	useEffect(() => {
		if (!isLastActionMessage && fg('jsm-kb-action-message-prop')) {
			return;
		}
		/** when new set of articleIds received */
		sendAnalyticsEvent({
			eventType: 'track',
			action: 'generated',
			actionSubject: 'jsmKbFromScratchArticles',
			actionSubjectId: 'jsmKbFromScratchArticlesGenerated',
			attributes: {
				projectId,
				activationId,
				articleIds: articleIds,
				articleCount: articleIds.length,
			},
		});
		void syncArticlesByIds(articleIds);
	}, [
		articleIds,
		syncArticlesByIds,
		sendAnalyticsEvent,
		projectId,
		activationId,
		isLastActionMessage,
	]);

	const setupHandler: RovoContentBridgeSetupHandler = useCallback(
		(on) => {
			on(JSMKBStagingAreaEventCommand, (payload: JSMKBStagingAreaEventCommandProperties) => {
				if (payload.senderId === JSM_CREATE_KB_STAGING_AREA_CLIENT_ID) {
					if (payload.type !== 'articlesUpdated' && payload.type !== 'articleDeleted') {
						return;
					}
					const updatedArticles = payload.data?.articles;
					const updatedArticleId = payload.data?.articleId;
					if (!Array.isArray(updatedArticles)) {
						return;
					}
					const resolvedUpdatedArticles = updatedArticles.filter(isKBArticleData);
					const publishableUpdatedArticles = toPublishableKbArticles(resolvedUpdatedArticles);
					setStagingAreaArticles(publishableUpdatedArticles);
					setAIFeatureContext({
						contextKey: JSM_CREATE_KB_ARTICLES_KEY,
						setContext: <T>(_existingContext: T): T =>
							({ kbDraftArticles: toKbDraftArticlesContext(resolvedUpdatedArticles) }) as T,
					});
					if (payload.type === 'articleDeleted') {
						if (!activationId || typeof updatedArticleId !== 'string' || !updatedArticleId) {
							return;
						}

						void createKbApi
							.deleteRovoSkillArticleById({
								projectId,
								activationId,
								articleId: updatedArticleId,
							})
							.then(() => {
								sendAnalyticsEvent({
									eventType: 'track',
									action: 'deleted',
									actionSubject: 'jsmKbFromScratchArticle',
									actionSubjectId: 'jsmKbFromScratchArticleDeleteSuccessful',
									attributes: {
										projectId,
										activationId,
										articleId: updatedArticleId,
									},
								});
							})
							.catch((error) => {
								logException({
									exception: error,
									name: 'CreateKbStagingAreaArticleDeleteError',
									tags: {
										projectId: String(projectId),
										articleId: updatedArticleId,
									},
								});
							});
						return;
					}

					if (!activationId || typeof updatedArticleId !== 'string' || !updatedArticleId) {
						return;
					}

					const updatedArticle = publishableUpdatedArticles.find(
						(article) => article.articleId === updatedArticleId,
					);
					if (
						!updatedArticle ||
						typeof updatedArticle.title !== 'string' ||
						updatedArticle.title.trim().length === 0 ||
						updatedArticle.body === undefined
					) {
						return;
					}

					sendAnalyticsEvent({
						eventType: 'track',
						action: 'edited',
						actionSubject: 'jsmKbFromScratchArticle',
						actionSubjectId: 'jsmKbFromScratchArticleEdited',
						attributes: {
							projectId,
							activationId,
							articleId: updatedArticleId,
						},
					});

					void createKbApi
						.updateRovoSkillArticleById({
							projectId,
							activationId,
							articleId: updatedArticleId,
							title: updatedArticle.title,
							body: updatedArticle.body,
							rovoTopicId: updatedArticle.rovoTopicId || null,
						})
						.catch((error) => {
							logException({
								exception: error,
								name: 'CreateKbStagingAreaArticleUpdateError',
								tags: {
									projectId: String(projectId),
									articleId: updatedArticleId,
								},
							});
						});
					return;
				}

				if (payload.senderId !== ROVO_CHAT_CLIENT_ID) {
					return;
				}

				const actionPayload = payload.data?.actions;
				if (!Array.isArray(actionPayload)) {
					return;
				}

				const payloadArticleIds = actionPayload
					.flatMap((action) => (action as JSMKBCreateItemsAction).data.articleIds ?? [])
					.filter((id): id is string => typeof id === 'string');
				if (!payloadArticleIds.length) {
					return;
				}

				void syncArticlesByIds(payloadArticleIds);
			});
		},
		[activationId, projectId, sendAnalyticsEvent, setAIFeatureContext, syncArticlesByIds],
	);

	return {
		stagingAreaArticles,
		setupHandler,
	};
};
